import React, { useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Modal, Alert, Platform } from 'react-native';
import AppTextInput from './AppTextInput';
import type { CareTask } from '../types/care';
import type { CareCircleMember } from '../types/care';

interface CareAddTaskModalProps {
  visible: boolean;
  onClose: () => void;
  onSave: (task: Omit<CareTask, 'id' | 'createdAt' | 'status'>) => void;
  /** Care circle members to assign task to (optional). */
  careMembers?: CareCircleMember[];
}

const CareAddTaskModal: React.FC<CareAddTaskModalProps> = ({
  visible,
  onClose,
  onSave,
  careMembers = [],
}) => {
  const [title, setTitle] = useState('');
  const [dueDate, setDueDate] = useState(() => {
    const d = new Date();
    return d.toISOString().slice(0, 10);
  });
  const [dueTime, setDueTime] = useState('');
  const [assignedToUserId, setAssignedToUserId] = useState<string>('');
  const [assignedToName, setAssignedToName] = useState<string>('');

  const handleSave = () => {
    const t = title.trim();
    if (!t) {
      Alert.alert('Required', 'Please enter a task title.');
      return;
    }
    onSave({
      title: t,
      dueDate,
      dueTime: dueTime.trim() || undefined,
      assignedToUserId: assignedToUserId || undefined,
      assignedToName: assignedToName || undefined,
    });
    setTitle('');
    setDueDate(new Date().toISOString().slice(0, 10));
    setDueTime('');
    setAssignedToUserId('');
    setAssignedToName('');
    onClose();
  };

  return (
    <Modal visible={visible} transparent animationType="slide">
      <View style={styles.overlay}>
        <View style={styles.card}>
          <View style={styles.header}>
            <Text style={styles.title}>Add task</Text>
            <TouchableOpacity onPress={onClose} hitSlop={12}>
              <Text style={styles.close}>✕</Text>
            </TouchableOpacity>
          </View>
          <Text style={styles.label}>Task</Text>
          <AppTextInput
            style={styles.input}
            value={title}
            onChangeText={setTitle}
            placeholder="e.g. Pick up prescription"
          />
          <Text style={styles.label}>Due date (YYYY-MM-DD)</Text>
          <AppTextInput
            style={styles.input}
            value={dueDate}
            onChangeText={setDueDate}
            placeholder="2025-02-10"
          />
          <Text style={styles.label}>Due time (optional, e.g. 14:00)</Text>
          <AppTextInput
            style={styles.input}
            value={dueTime}
            onChangeText={setDueTime}
            placeholder="14:00"
          />
          {careMembers.length > 0 && (
            <>
              <Text style={styles.label}>Assign to (optional)</Text>
              <View style={styles.assignRow}>
                <TouchableOpacity
                  style={[styles.assignOption, !assignedToUserId && styles.assignOptionOn]}
                  onPress={() => { setAssignedToUserId(''); setAssignedToName(''); }}
                >
                  <Text style={styles.assignOptionText}>Unassigned</Text>
                </TouchableOpacity>
                {careMembers.filter((m) => m.status === 'active' && m.userId).map((m) => (
                  <TouchableOpacity
                    key={m.id}
                    style={[styles.assignOption, assignedToUserId === m.userId && styles.assignOptionOn]}
                    onPress={() => { setAssignedToUserId(m.userId); setAssignedToName(m.name); }}
                  >
                    <Text style={styles.assignOptionText}>{m.name}</Text>
                  </TouchableOpacity>
                ))}
              </View>
            </>
          )}
          <View style={styles.footer}>
            <TouchableOpacity style={styles.cancelBtn} onPress={onClose}>
              <Text style={styles.cancelBtnText}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.saveBtn} onPress={handleSave}>
              <Text style={styles.saveBtnText}>Add task</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.4)',
    justifyContent: 'flex-end',
  },
  card: {
    backgroundColor: '#fff',
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    padding: 20,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  title: { fontSize: 20, fontWeight: '700', color: '#1C1C1E' },
  close: { fontSize: 22, color: '#8E8E93', padding: 4 },
  label: { fontSize: 14, fontWeight: '600', color: '#1C1C1E', marginBottom: 6 },
  input: {
    borderWidth: 1,
    borderColor: '#E5E5E7',
    borderRadius: 10,
    padding: 12,
    fontSize: 16,
    color: '#1C1C1E',
    marginBottom: 16,
  },
  footer: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 8,
  },
  cancelBtn: { flex: 1, padding: 14, alignItems: 'center', borderRadius: 10, backgroundColor: '#F2F2F7' },
  cancelBtnText: { fontSize: 16, fontWeight: '600', color: '#1C1C1E' },
  saveBtn: { flex: 1, padding: 14, alignItems: 'center', borderRadius: 10, backgroundColor: '#007AFF' },
  saveBtnText: { fontSize: 16, fontWeight: '600', color: '#fff' },
  assignRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 16 },
  assignOption: { paddingHorizontal: 12, paddingVertical: 8, borderRadius: 8, backgroundColor: '#F2F2F7' },
  assignOptionOn: { backgroundColor: '#007AFF' },
  assignOptionText: { fontSize: 14, color: '#1C1C1E' },
});

export default CareAddTaskModal;
